import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { confirmModal } from '@/components/ui/modal'
import { fmtMoney } from '../../shared/format'
import { useDeleteFollowup, useDeleteInvoice, useDeleteMeeting, useDeleteModule, useDeleteOffer, useDeleteOrder, useDeleteReceipt, useDeleteRenewal, useDeleteRequest, useDeleteWork } from '../api'
import type { CustomerModule, Followup, Invoice, Meeting, Offer, Order, Receipt, Renewal, SupportRequest, WorkRecord } from '../types'
import { toastError } from '../ui'

/** Onay modalı + silme; açıklama neyin etkileneceğini söyler. */
export function useCrmDeletes() {
  const { t } = useTranslation()
  const meeting = useDeleteMeeting()
  const offer = useDeleteOffer()
  const module = useDeleteModule()
  const work = useDeleteWork()
  const request = useDeleteRequest()
  const invoice = useDeleteInvoice()
  const receipt = useDeleteReceipt()
  const order = useDeleteOrder()
  const renewal = useDeleteRenewal()
  const followup = useDeleteFollowup()

  const run = async (title: string, description: string, fn: () => Promise<unknown>) => {
    if (!(await confirmModal({ title, description, confirmLabel: t('common.delete'), destructive: true }))) return false
    try {
      await fn()
      toast.success(t('common.deleted'))
      return true
    } catch (e) {
      toastError(e)
      return false
    }
  }
  return {
    meeting: (m: Meeting) => run(t('crm.del.meeting'), t('crm.del.meetingBody', { date: m.date }), () => meeting.mutateAsync(m.id)),
    offer: (o: Offer) => run(t('crm.del.offer'), t('crm.del.offerBody', { number: o.number }), () => offer.mutateAsync(o.id)),
    module: (m: CustomerModule) => run(t('crm.del.module'), t('crm.del.moduleBody', { name: m.name }), () => module.mutateAsync(m.id)),
    work: (w: WorkRecord) => run(t('crm.del.work'), t('crm.del.workBody', { kind: t(`crm.workKind.${w.kind}`), date: w.date }), () => work.mutateAsync(w.id)),
    request: (r: SupportRequest) => run(t('crm.del.request'), t('crm.del.requestBody', { number: r.number }), () => request.mutateAsync(r.id)),
    invoice: (i: Invoice) => run(t('crm.del.invoice'), t('crm.del.invoiceBody', { number: i.number }), () => invoice.mutateAsync(i.id)),
    order: (o: Order) => run(t('crm.del.order'), t('crm.del.orderBody', { number: o.number }), () => order.mutateAsync(o.id)),
    renewal: (r: Renewal) => run(t('crm.del.renewal'), t('crm.del.renewalBody', { kind: t(`crm.renewalKind.${r.kind}`), date: r.dueOn }), () => renewal.mutateAsync(r.id)),
    followup: (f: Followup) => run(t('crm.del.followup'), t('crm.del.followupBody', { note: f.note }), () => followup.mutateAsync(f.id)),
    receipt: (r: Receipt) => run(t('crm.del.receipt'), t('crm.del.receiptBody', { amount: fmtMoney(r.amount) }), () => receipt.mutateAsync(r.id)),
  }
}
